<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

/**
 * The avatar menu.
 *
 * Built rather than layered onto UI/Dropdown, which is a positioning primitive
 * with no roles, no keyboard handling and no focus management — and which the
 * old sidebar already bypassed with hand-computed coordinates. A menu that
 * signs people out should be reachable without a mouse.
 */
const props = defineProps({
  open: { type: Boolean, required: true },
  labelledBy: { type: String, default: null },
});

const emit = defineEmits(['update:open']);

const router = useRouter();
const menu = ref(null);

const items = () => Array.from(menu.value?.querySelectorAll('[role="menuitem"]') ?? []);

const close = ({ restoreFocus = true } = {}) => {
  emit('update:open', false);
  if (restoreFocus && props.labelledBy) {
    // Focus goes back where it came from, so closing does not dump the caret
    // at the top of the document.
    document.getElementById(props.labelledBy)?.focus();
  }
};

const onKeydown = (event) => {
  const focusable = items();
  if (!focusable.length) return;
  const index = focusable.indexOf(document.activeElement);

  if (event.key === 'Escape') {
    event.preventDefault();
    close();
  } else if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusable[(index + 1) % focusable.length].focus();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusable[(index - 1 + focusable.length) % focusable.length].focus();
  } else if (event.key === 'Home') {
    event.preventDefault();
    focusable[0].focus();
  } else if (event.key === 'End') {
    event.preventDefault();
    focusable[focusable.length - 1].focus();
  }
};

// Bound only while open. The old nav kept a document listener alive for the
// life of the component whether or not anything could be dismissed.
const onPointerDown = (event) => {
  if (!menu.value) return;
  const trigger = props.labelledBy ? document.getElementById(props.labelledBy) : null;
  if (menu.value.contains(event.target) || trigger?.contains(event.target)) return;
  close({ restoreFocus: false });
};

// immediate, so a menu that renders already-open still takes focus and binds
// its dismissal listener. Without it both depend on the open state having
// changed after mount, which is true of this one caller and need not be of
// the next.
watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      document.addEventListener('mousedown', onPointerDown);
      await nextTick();
      items()[0]?.focus();
    } else {
      document.removeEventListener('mousedown', onPointerDown);
    }
  },
  { immediate: true }
);

// A menu that survives navigation hangs over the next page.
const stopAfterEach = router.afterEach(() => {
  if (props.open) close({ restoreFocus: false });
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onPointerDown);
  stopAfterEach();
});
</script>

<template>
  <div
    v-if="open"
    ref="menu"
    role="menu"
    :aria-labelledby="labelledBy"
    class="hub-menu"
    data-xl-gen="northstar"
    @keydown="onKeydown"
  >
    <slot />
  </div>
</template>

<style scoped>
/* Positioned against the avatar, which sits 12px above the top bar's bottom
   edge; the menu hangs 5px under the bar. */
.hub-menu {
  position: absolute;
  top: calc(100% + 17px);
  right: 0;
  z-index: 60;
  min-width: 214px;
  min-height: 0;
  padding: 6px;
  border-radius: 14px;
  border: 1px solid var(--xl-color-border);
  background: #fff;
  color: var(--xl-color-primary-text);
  box-shadow: 0 18px 44px rgba(28, 23, 32, 0.16);
}
</style>
